<!-- Notifications toggle -->
<button mat-icon-button (click)="openPanel()" #notificationsOrigin>
    @if (unreadCount > 0) {
        <span
            class="absolute left-0 right-0 top-0 flex h-3 items-center justify-center"
        >
            <span
                class="ml-4 mt-2.5 flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full bg-teal-600 px-1 text-xs font-medium text-indigo-50"
            >
                {{ unreadCount }}
            </span>
        </span>
    }
    <mat-icon [svgIcon]="'heroicons_outline:bell'"></mat-icon>
</button>

<!-- Notifications panel -->
<ng-template #notificationsPanel>
    <div
        class="fixed inset-0 flex flex-col overflow-hidden shadow-lg sm:static sm:inset-auto sm:w-90 sm:min-w-90 sm:rounded-2xl"
    >
        <!-- Header -->
        <div
            class="flex shrink-0 items-center bg-primary py-4 pl-6 pr-4 text-on-primary"
        >
            <div class="-ml-1 mr-3 sm:hidden">
                <button mat-icon-button (click)="closePanel()">
                    <mat-icon
                        class="text-current icon-size-5"
                        [svgIcon]="'heroicons_solid:x-mark'"
                    ></mat-icon>
                </button>
            </div>
            <div class="text-lg font-medium leading-10">Notifications</div>
            <div class="ml-auto flex items-center">
                @if (canSend) {
                    <button
                        class="dark:text-white"
                        mat-icon-button
                        [matTooltip]="'New notification'"
                        (click)="compose()"
                    >
                        <mat-icon
                            class="text-current icon-size-5"
                            [svgIcon]="'heroicons_solid:pencil-square'"
                        ></mat-icon>
                    </button>
                }
                <button
                    class="dark:text-white"
                    mat-icon-button
                    [matTooltip]="'Mark all as read'"
                    [disabled]="unreadCount === 0"
                    (click)="markAllAsRead()"
                >
                    <mat-icon
                        class="text-current icon-size-5"
                        [svgIcon]="'heroicons_solid:envelope-open'"
                    ></mat-icon>
                </button>
            </div>
        </div>

        <!-- Content -->
        <div
            class="bg-card relative flex flex-auto flex-col divide-y overflow-y-auto sm:max-h-120"
        >
            <!-- Notifications -->
            @for (notification of notifications; track notification.id) {
                <div
                    class="group flex hover:bg-gray-50 dark:hover:bg-black dark:hover:bg-opacity-5"
                    [ngClass]="{ unread: !notification.read }"
                >
                    <!-- Content (clickable when it links to a page) -->
                    <div
                        class="flex flex-auto py-5 pl-6"
                        [ngClass]="{ 'cursor-pointer': notification.link }"
                        (click)="notification.link && open(notification)"
                    >
                        <!-- Sender initials -->
                        <div
                            class="mr-4 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100 text-sm font-semibold dark:bg-gray-700"
                        >
                            {{ initials(notification.senderName) }}
                        </div>
                        <!-- Sender, message & time -->
                        <div class="flex min-w-0 flex-auto flex-col">
                            <div class="line-clamp-1 font-semibold">
                                {{ notification.senderName }}
                            </div>
                            @if (notification.replyToMessage) {
                                <div class="text-secondary line-clamp-1 text-sm italic">
                                    Re: {{ notification.replyToMessage }}
                                </div>
                            }
                            <div class="line-clamp-2 whitespace-pre-line break-words">
                                {{ notification.message }}
                            </div>
                            <div
                                class="text-secondary mt-2 flex items-center gap-1 text-sm leading-none"
                            >
                                {{ notification.createdUtc | date: 'MMM dd, h:mm a' }}
                                @if (notification.link) {
                                    <mat-icon
                                        class="icon-size-4"
                                        [svgIcon]="'heroicons_outline:arrow-top-right-on-square'"
                                    ></mat-icon>
                                }
                            </div>
                        </div>
                    </div>

                    <!-- Actions -->
                    <div class="relative my-5 ml-2 mr-6 flex flex-col">
                        <!-- Indicator -->
                        <button
                            class="h-6 min-h-6 w-6"
                            mat-icon-button
                            (click)="toggleRead(notification)"
                            [matTooltip]="
                                notification.read
                                    ? 'Mark as unread'
                                    : 'Mark as read'
                            "
                        >
                            <span
                                class="h-2 w-2 rounded-full"
                                [ngClass]="{
                                    'bg-gray-400 dark:bg-gray-500 sm:opacity-0 sm:group-hover:opacity-100':
                                        notification.read,
                                    'bg-primary': !notification.read,
                                }"
                            ></span>
                        </button>
                        <!-- Reply -->
                        <button
                            class="h-6 min-h-6 w-6 sm:opacity-0 sm:group-hover:opacity-100"
                            mat-icon-button
                            (click)="reply(notification)"
                            [matTooltip]="'Reply'"
                        >
                            <mat-icon
                                class="icon-size-4"
                                [svgIcon]="'heroicons_solid:arrow-uturn-left'"
                            ></mat-icon>
                        </button>
                        <!-- Delete -->
                        <button
                            class="h-6 min-h-6 w-6 sm:opacity-0 sm:group-hover:opacity-100"
                            mat-icon-button
                            (click)="delete(notification)"
                            [matTooltip]="'Delete'"
                        >
                            <mat-icon
                                class="icon-size-4"
                                [svgIcon]="'heroicons_solid:x-mark'"
                            ></mat-icon>
                        </button>
                    </div>
                </div>
            }

            <!-- No notifications -->
            @if (!notifications || !notifications.length) {
                <div
                    class="flex flex-auto flex-col items-center justify-center px-8 py-12 sm:justify-start"
                >
                    <div
                        class="flex h-14 w-14 flex-0 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-600"
                    >
                        <mat-icon
                            class="text-primary-700 dark:text-primary-50"
                            [svgIcon]="'heroicons_outline:bell'"
                        ></mat-icon>
                    </div>
                    <div class="mt-5 text-2xl font-semibold tracking-tight">
                        No notifications
                    </div>
                    <div
                        class="text-secondary mt-1 w-full max-w-60 text-center text-md"
                    >
                        When colleagues send you notifications, they will appear here.
                    </div>
                </div>
            }
        </div>

        <!-- Footer -->
        @if (canSend) {
            <div class="bg-card flex shrink-0 justify-center border-t py-2">
                <a
                    class="text-primary px-4 py-2 font-medium hover:underline"
                    [routerLink]="['/notifications']"
                    (click)="closePanel()"
                >
                    View all
                </a>
            </div>
        }
    </div>
</ng-template>
